import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { landingPagesApi } from '@/api/landingPages';
import { Button, Input } from '@/components/ui';
import type { LandingPageType } from '@/types';

const SLUG_PATTERN = /^[a-z0-9-]+$/;

function duplicateSlugMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: string }).code === '23505'
  ) {
    return 'Slug is already taken.';
  }

  const message = error instanceof Error ? error.message : String(error);
  if (message.toLowerCase().includes('duplicate')) return 'Slug is already taken.';
  return message;
}

export default function LandingPageBuilderPage() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<LandingPageType>('waitlist');
  const [slugError, setSlugError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const validateSlug = () => {
    if (!slug.trim()) return 'Slug is required.';
    if (slug.length > 60) return 'Slug must be 60 characters or less.';
    if (!SLUG_PATTERN.test(slug)) return 'Use lowercase letters, numbers, and hyphens only.';
    return '';
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    const nextSlugError = validateSlug();
    setSlugError(nextSlugError);
    if (nextSlugError || !title.trim()) return;

    setSaving(true);
    try {
      const page = await landingPagesApi.create({
        slug,
        title: title.trim(),
        type,
      });
      navigate(`/landing-pages/${page.id}`);
    } catch (error) {
      setFormError(duplicateSlugMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div dir="ltr" className="mx-auto max-w-lg">
      <Link
        to="/landing-pages"
        className="mb-4 inline-flex items-center gap-2 text-sm text-ink-secondary transition-colors hover:text-gold"
      >
        <ArrowLeft className="h-4 w-4" />
        Landing Pages
      </Link>

      <form onSubmit={handleSubmit} className="card space-y-5 p-6">
        <div>
          <h1 className="text-xl font-semibold text-ink-primary">New landing page</h1>
          <p className="mt-1 text-sm text-ink-secondary">Create a draft landing page shell.</p>
        </div>

        <Input
          label="Slug"
          value={slug}
          onChange={(event) => {
            setSlug(event.target.value.toLowerCase());
            setSlugError('');
          }}
          maxLength={60}
          required
          error={slugError}
          hint="Lowercase letters, numbers, and hyphens only."
        />

        <Input
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          required
        />

        <div>
          <label htmlFor="landing-page-type" className="mb-1.5 block text-sm font-medium text-ink-secondary">
            Type
          </label>
          <select
            id="landing-page-type"
            value={type}
            onChange={(event) => setType(event.target.value as LandingPageType)}
            className="h-10 w-full rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary focus:outline-none focus:ring-2 focus:ring-gold/40"
          >
            <option value="waitlist">Waitlist</option>
            <option value="limited">Limited free signup</option>
          </select>
        </div>

        {formError && (
          <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
            {formError}
          </div>
        )}

        <Button type="submit" loading={saving} fullWidth>
          Create landing page
        </Button>
      </form>
    </div>
  );
}
